<template>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div :key="mermaidTheme" ref="body" class="markdown-body" v-html="renderedHtml" />
</template>
<script setup lang="ts">
import {
    computed,
    getCurrentInstance,
    inject,
    onMounted,
    onUpdated,
    shallowRef,
    toRef,
    triggerRef,
    watch,
} from "vue";
import type { MarkdownIt as MarkdownItType } from "markdown-it";
import { useUserLookups } from "@/queries/users";
import { useRouter } from "vue-router";
import { useProjectsStore } from "@/store/projects";
import * as markdownPlugins from "@/markdown";
import type { MarkdownAttachment } from "@/markdown";
import type { AttachmentId } from "@/api/tickets";
import { injectProject } from "@/composables/project";
import { AttachmentProvideKey } from "@/composables/attachments";
import { bindTicketHover } from "@/composables/ticketHoverCard";
import { useTheme } from "vuetify";
// Partially based off of https://github.com/miaolz123/vue-markdown

type Props = {
    source: string;
    attachments?: readonly MarkdownAttachment[];
};

const props = withDefaults(defineProps<Props>(), {
    attachments: () => [],
});

const userLookups = useUserLookups();
const projects = useProjectsStore();
const theme = useTheme();

// Mermaid bakes its palette into the rendered SVG, so it has to be re-initialised
// and the diagrams re-rendered whenever the app flips between light and dark.
const mermaidTheme = computed(() => (theme.current.value.dark ? "dark" : "default"));

const md = shallowRef<MarkdownItType>();

// so we can dynamically load users
watch(userLookups.users, (u) => {
    if (md.value) {
        md.value.use(markdownPlugins.userMentions, u ?? new Map());
        md.value.use(markdownPlugins.quoteBlocks, u ?? new Map());
        triggerRef(md);
    }
});

const projectProvide = injectProject();
const attachmentProvide = inject(AttachmentProvideKey, undefined);

// and same for projects
watch(
    () => projects.projects,
    (p) => {
        if (md.value) {
            md.value.use(
                markdownPlugins.ticketMentions,
                p ?? new Map(),
                projectProvide?.project.value
            );
            triggerRef(md);
        }
    }
);

const renderedHtml = computed(() => (md.value ? md.value.render(props.source) : ""));

if (projectProvide) {
    watch(projectProvide.project, (p) => {
        md.value?.use(markdownPlugins.ticketMentions, projects.projects ?? new Map(), p);
        triggerRef(md);
    });
}

const attachments = toRef(props, "attachments");

watch(
    () => attachments.value,
    (a) => setMd({ ...props, attachments: a })
);
onMounted(update);
onUpdated(update);

let ready = false;

const { default: MarkdownIt } = await import("markdown-it");
const { default: hljs } = await import("highlight.js");
const { full: emoji } = await import("markdown-it-emoji");
const { default: textualUml } = await import("markdown-it-textual-uml");
// The package is CommonJS with an `exports.default`, so importing it hands back the
// module object rather than the plugin; markdown-it then calls apply() on it and throws.
const katexModule = await import("@vscode/markdown-it-katex");
const katex =
    (katexModule.default as unknown as { default?: typeof katexModule.default }).default ??
    katexModule.default;
const { default: mermaid } = await import("mermaid");

function formatCode(code: string) {
    const formattedCode = code
        .replace(/(?<!=\s*)(&gt;<\/span>)(?![\n\s]|{{)/g, "$1\n")
        .replace(/(?<!&gt)(?<!&lt)(?<!&amp);(?![\n\s])(?=(?:[^'"]|'[^']*'|"[^"]*")*$)/g, ";\n");
    return formattedCode;
}

function addLineClass(pre: HTMLElement) {
    const codeElement = pre.querySelector("code");
    if (!codeElement) return;
    if (codeElement.innerHTML.includes('<span class="line">')) return;
    const innerHTML = codeElement.innerHTML;
    const formatted = formatCode(innerHTML).trimEnd().trimStart();
    const lines = formatted.split("\n");

    const updatedCode = lines.map((line) => `<span class="line">${line || ""}</span>`).join("\n"); // Join with newline characters to preserve line breaks

    codeElement.innerHTML = updatedCode;
}

mermaid.initialize({ startOnLoad: false, theme: mermaidTheme.value });

watch(mermaidTheme, (t) => mermaid.initialize({ startOnLoad: false, theme: t }));

ready = true;

setMd(props);

function setMd(p: typeof props): void {
    if (!ready) {
        return;
    }

    md.value = new MarkdownIt({ linkify: true })
        .use(emoji)
        .use(katex)
        .use(textualUml)
        .use(markdownPlugins.colorSpans)
        .use(markdownPlugins.userMentions, userLookups.users.value ?? new Map())
        .use(
            markdownPlugins.ticketMentions,
            projects.projects ?? new Map(),
            projectProvide?.project.value
        )
        .use(markdownPlugins.quoteBlocks, userLookups.users.value ?? new Map())
        .set({
            highlight: function (str, lang) {
                if (lang && hljs.getLanguage(lang)) {
                    try {
                        return hljs.highlight(str, { language: lang }).value;
                    } catch {
                        // ignore
                    }
                }

                return "";
            },
        });

    if (p.attachments.length > 0) {
        md.value.use(markdownPlugins.attachmentLinks, p.attachments);
        const originalImage = md.value.renderer.rules.image;
        if (originalImage) {
            md.value.use(markdownPlugins.attachmentImages, p.attachments, originalImage);
        }
    }
}

// modified clicks (ctrl/cmd/shift/alt or non-left button) keep the anchor's
// native behavior so open-in-new-tab works
function isModifiedClick(ev: Event): boolean {
    return (
        ev instanceof MouseEvent &&
        (ev.button !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey)
    );
}

// An image the stylesheet had to shrink gets wrapped so the badge can sit on it,
// and offers the full-size view the cap took away. Images that fit are untouched.
function bindOversizedImages(body: HTMLDivElement): void {
    body.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
        // The file-card poster is deliberately cropped to its own size.
        if (img.closest(".attachment-file") || img.dataset.expandBound) {
            return;
        }
        img.dataset.expandBound = "true";

        if (img.complete) {
            markOversizedImage(img);
        } else {
            img.addEventListener("load", () => markOversizedImage(img), { once: true });
        }
    });
}

function markOversizedImage(img: HTMLImageElement): void {
    const cap = Number.parseFloat(getComputedStyle(img).maxHeight);
    if (!Number.isFinite(cap) || img.naturalHeight <= cap) {
        return;
    }

    // The cap only bit if it, rather than the available width, decided the height.
    if (Math.abs(img.getBoundingClientRect().height - cap) > 1.5) {
        return;
    }

    const wrapper = document.createElement("span");
    wrapper.className = "markdown-image";
    img.replaceWith(wrapper);
    wrapper.append(img);

    const badge = document.createElement("span");
    badge.className = "markdown-image__expand";
    badge.innerHTML = `<i class="mdi mdi-arrow-expand" aria-hidden="true"></i>`;
    badge.append("Click to expand");
    wrapper.append(badge);

    // Attachments already open in the lightbox; anything else has no viewer to
    // reach, so the image itself is the only full-size copy available.
    if (!img.classList.contains("attachment")) {
        img.addEventListener("click", () => window.open(img.src, "_blank", "noopener"));
    }
}

async function update(): Promise<void> {
    // doing it this way, otherwise we'd have to use an inline click or something weird
    const instance = getCurrentInstance();
    if (!instance) {
        return;
    }

    const router = useRouter();

    const body = instance.refs["body"] as HTMLDivElement | null;
    if (!body) {
        return;
    }

    // Without a preview provider (the portal) attachment anchors keep their native
    // behavior; hijacking the click would turn inline file cards into dead links.
    if (attachmentProvide) {
        body.querySelectorAll("img.attachment, a.attachment").forEach((m) => {
            const el = m as HTMLElement;
            // Bind once: update() re-runs on every render and these nodes persist.
            if (el.dataset.attachmentBound) {
                return;
            }
            el.dataset.attachmentBound = "true";

            const attachmentId = m.getAttribute("data-attachment-id");
            m.addEventListener("click", (ev) => {
                ev.preventDefault();
                attachmentProvide.shownAttachment.value = attachmentId as AttachmentId | null;
            });
        });
    }
    bindOversizedImages(body);
    body.querySelectorAll("a.user-mention").forEach((m) => {
        m.addEventListener("click", (ev) => {
            if (isModifiedClick(ev)) {
                return;
            }
            ev.preventDefault();
            void router.push({
                name: "UserView",
                params: { userId: m.getAttribute("data-user-name") },
            });
        });
    });
    body.querySelectorAll("a.ticket-mention").forEach((m) => {
        m.addEventListener("click", (ev) => {
            if (isModifiedClick(ev)) {
                return;
            }
            ev.preventDefault();
            void router.push({
                name: "TicketView",
                params: { ticketId: m.getAttribute("data-ticket-id") },
            });
        });

        const el = m as HTMLElement;
        const key = el.getAttribute("data-ticket-id");
        // Bind once: update() re-runs on every render and these nodes persist.
        if (key && !el.dataset.hoverBound) {
            el.dataset.hoverBound = "true";
            bindTicketHover(el, key);
        }
    });
    body.querySelectorAll("a.ticket-quote__source").forEach((m) => {
        const commentId = m.getAttribute("data-comment-id");
        const target = commentId ? document.getElementById(`comment-${commentId}`) : null;
        if (!target) {
            // The quoted comment isn't on this page: degrade to a non-jumping label.
            m.removeAttribute("href");
            m.classList.add("ticket-quote__source--static");
            return;
        }
        m.addEventListener("click", (ev) => {
            ev.preventDefault();
            target.scrollIntoView({ behavior: "smooth", block: "center" });
            target.classList.remove("ticket-comment--flash");
            void target.offsetWidth;
            target.classList.add("ticket-comment--flash");
        });
    });
    body.querySelectorAll("pre").forEach((e) => {
        addLineClass(e);
    });

    await mermaid.run();
}
</script>
<style lang="scss">
// The github-markdown palette variants are pinned per theme; the package's default
// stylesheet keys its colours off prefers-color-scheme, which fights the
// runtime-toggled Vuetify theme.
.v-theme--procyonLightTheme {
    @import "highlight.js/scss/github";
    @import "github-markdown-css/github-markdown-light";
}

.v-theme--procyonDarkTheme,
.v-theme--diphyllaTheme {
    @import "highlight.js/scss/github-dark";
    @import "github-markdown-css/github-markdown-dark";
}

.markdown-body.markdown-body {
    // undo the github-markdown font change; inherit the app's body font
    // (an unconfigured @use of vuetify's variables would resolve to Roboto,
    // not the IBM Plex Sans the app is compiled with)
    font-family: inherit;

    // The github stylesheet paints its own canvas color; rendered markdown should
    // sit directly on the card surface instead.
    background-color: transparent;

    // Keep code blocks readable on the bare surface with a subtle,
    // theme-variable-based tint that tracks the active Vuetify theme.
    pre,
    code {
        background-color: rgba(var(--v-theme-on-surface), 0.06);
    }

    // Don't double-stack the tint for code inside pre blocks.
    pre code {
        background-color: transparent;
    }
}
.markdown-body pre code {
    text-wrap: pretty !important;
    white-space: pre-wrap !important;
    overflow-wrap: break-word;
}

// A phone screenshot is narrow enough to clear max-width and still render at its
// full height, burying the rest of the text. An explicit size in the markdown
// (?height=, ?max-height=) writes an inline style and still wins over this.
.markdown-body img {
    max-height: 20rem;
    width: auto;
}

.markdown-image {
    position: relative;
    display: inline-block;
    max-width: 100%;
    line-height: 0;

    img {
        cursor: zoom-in;
    }

    &__expand {
        position: absolute;
        right: 0.5rem;
        bottom: 0.5rem;
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.1875rem 0.625rem;
        border: thin solid rgba(255, 255, 255, 0.16);
        border-radius: 999px;
        background: rgba(12, 17, 30, 0.72);
        backdrop-filter: blur(4px);
        color: #fff;
        font-size: 0.75rem;
        font-weight: 600;
        line-height: 1.5;
        pointer-events: none;
    }
}

.markdown-body .ticket-quote {
    margin: 0.5rem 0;
    padding: 0.35rem 0.85rem;
    border-left: 3px solid rgba(var(--v-theme-primary), 0.8);
    background-color: rgba(var(--v-theme-on-surface), 0.045);
    border-radius: 0 4px 4px 0;

    .ticket-quote__attr {
        margin-bottom: 0.2rem;
        font-size: 0.82rem;
    }

    .ticket-quote__source {
        color: rgba(var(--v-theme-primary), 1);
        font-weight: 600;
        text-decoration: none;
    }

    .ticket-quote__source:hover {
        text-decoration: underline;
    }

    .ticket-quote__source--static {
        cursor: default;
    }

    .ticket-quote__source--static:hover {
        text-decoration: none;
    }

    .ticket-quote__said {
        margin-left: 0.35rem;
        font-weight: 400;
        color: rgba(var(--v-theme-on-surface), 0.6);
    }

    > p:last-child {
        margin-bottom: 0;
    }
}

pre {
    tab-size: 4;
    counter-reset: lineCounter;
    padding: 0;
    font-size: 0.875rem;
    line-height: 1.25rem;
}

pre span.line {
    counter-increment: lineCounter;
    line-height: 1.25rem;
}

pre span.line::before {
    content: counter(lineCounter);
    color: rgba(var(--v-theme-primary-lighten-1), 0.8) !important;
    width: 2rem;
    display: inline-block;
    border-right: 0.0625rem dotted rgba(var(--v-theme-secondary-darken-1), 0.4);
    padding-right: 0.25rem;
    margin-right: 0.5rem;
    text-align: right;
    font-size: 0.85rem;
    line-height: 1.25rem;
}

pre span.line:nth-child(odd)::before {
    background-color: rgba(var(--v-theme-surface-darken-2), 0.7) !important;
}

.attachment-video,
.attachment-audio {
    display: block;
    max-width: 100%;
    margin: 0.5rem 0;
    border-radius: 0.5rem;
}

.attachment-video {
    background: #000;
}

.attachment-file {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    max-width: 22rem;
    margin: 0.5rem 0;
    padding: 0.5rem 0.75rem;
    border: thin solid rgba(var(--v-theme-on-surface), 0.16);
    border-radius: 0.5rem;
    text-decoration: none;
    color: inherit;

    &:hover {
        border-color: rgba(var(--v-theme-primary), 0.5);
    }

    &__icon {
        font-size: 1.75rem;
        color: rgba(var(--v-theme-on-surface), 0.6);
    }

    &__name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: rgb(var(--v-theme-primary));
    }

    &__size {
        margin-left: auto;
        font-size: 0.75rem;
        color: rgba(var(--v-theme-on-surface), 0.5);
    }

    // A file that rendered a poster (a PDF's first page) shows it above the caption instead.
    &:has(.attachment-file__preview) {
        display: block;
        padding: 0;
        overflow: hidden;
    }

    &__preview img {
        display: block;
        width: 100%;
        max-height: 15rem;
        object-fit: cover;
        object-position: top;
    }

    &__caption {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        border-top: thin solid rgba(var(--v-theme-on-surface), 0.12);
    }
}
</style>
